// M146 Fase 3 — Wrapper-pagina's opnieuw gestyled met universele PageShell.
// Elke wrapper zet EmbedContext (`embedded: true, hideInnerHeader: true`)
// zodat het onderliggende component alleen zijn body rendert; header +
// tabs zitten hier in de wrapper.
import { useSearchParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { EmbedContext } from "@/contexts/EmbedContext";
import { PageShell } from "@/components/ui/PageShell";
import { MessageCircleQuestion } from "lucide-react";

import AdviezenPage from "@/pages/AdviezenPage";
import PaginaFocusPage from "@/pages/PaginaFocusPage";
import MerkstemPage from "@/pages/MerkstemPage";
import VlogsPage from "@/pages/VlogsPage";
import BerichtenPage from "@/pages/BerichtenPage";
import AdsVoorstellenPage from "@/pages/AdsVoorstellenPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import EffectPage from "@/pages/EffectPage";
import SeoOverviewPage from "@/pages/SeoOverviewPage";
import MarketRadarPage from "@/pages/MarketRadarPage";
import SiteAuditPage from "@/pages/SiteAuditPage";
import PrivacyVaultPage from "@/pages/PrivacyVaultPage";
import WalletPage from "@/pages/WalletPage";
import BedrijfsprofielView from "@/pages/BedrijfsprofielView";

function Tabs({ tabs, current, onChange, testid = "tabs" }) {
  return (
    <div
      className="border-b border-border-hairline flex gap-6 overflow-x-auto mb-8 -mx-2 px-2 scrollbar-none"
      style={{ WebkitOverflowScrolling: "touch" }}
      role="tablist"
      aria-label="Sectie-tabs"
      data-testid={testid}
    >
      {tabs.map((tab) => {
        const active = current === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            data-testid={`${testid}-${tab.key}`}
            role="tab"
            aria-selected={active}
            aria-controls={`${testid}-panel-${tab.key}`}
            id={`${testid}-tab-${tab.key}`}
            tabIndex={active ? 0 : -1}
            className={`pb-3 -mb-px text-sm font-medium whitespace-nowrap transition-colors ${
              active
                ? "text-accent-teal border-b-2 border-accent-teal"
                : "text-text-muted hover:text-text-primary"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function TabPanel({ testid, tabKey, children }) {
  return (
    <div
      role="tabpanel"
      id={`${testid}-panel-${tabKey}`}
      aria-labelledby={`${testid}-tab-${tabKey}`}
      tabIndex={0}
    >
      {children}
    </div>
  );
}

function useTab(defaultKey) {
  const [sp, setSp] = useSearchParams();
  const cur = sp.get("tab") || defaultKey;
  const set = (k) => { const n = new URLSearchParams(sp); n.set("tab", k); setSp(n); };
  return [cur, set];
}

function EmbedShell({ children }) {
  return (
    <EmbedContext.Provider value={{ embedded: true, hideInnerHeader: true }}>
      {children}
    </EmbedContext.Provider>
  );
}

// ---------- Content ---------------------------------------------------------
const CONTENT_TABS = [
  { key: "adviezen", label: "Adviezen" },
  { key: "pagina-focus", label: "Pagina-focus" },
  { key: "merkstem", label: "Merkstem" },
  { key: "vlogs", label: "Vlogs" },
  { key: "berichten", label: "Berichten" },
  { key: "advertenties", label: "Advertentie-voorstellen" },
];

export function ContentHubPage() {
  const [tab, setTab] = useTab("adviezen");
  return (
    <AppLayout>
      <PageShell
        eyebrow="JOUW CONTENT"
        title="Content"
        subtitle="Adviezen, pagina-focus, merkstem, vlogs, berichten en Ads-voorstellen op één plek."
      >
        <Tabs tabs={CONTENT_TABS} current={tab} onChange={setTab} testid="content-tabs" />
        <EmbedShell>
          <TabPanel testid="content-tabs" tabKey={tab}>
            {tab === "adviezen"     && <AdviezenPage />}
            {tab === "pagina-focus" && <PaginaFocusPage />}
            {tab === "merkstem"     && <MerkstemPage />}
            {tab === "vlogs"        && <VlogsPage />}
            {tab === "berichten"    && <BerichtenPage />}
            {tab === "advertenties" && <AdsVoorstellenPage />}
          </TabPanel>
        </EmbedShell>
      </PageShell>
    </AppLayout>
  );
}

// ---------- Kanalen ---------------------------------------------------------
export function KanalenPage() {
  return (
    <AppLayout>
      <PageShell
        eyebrow="JOUW KANALEN"
        title="Kanalen"
        subtitle="Koppel Google Search Console, Analytics en de vlog-generator. Meer kanalen volgen."
      >
        <EmbedShell><IntegrationsPage /></EmbedShell>
      </PageShell>
    </AppLayout>
  );
}

// ---------- Resultaten ------------------------------------------------------
const RESULTATEN_TABS = [
  { key: "effect", label: "Effect per pagina" },
  { key: "seo",    label: "SEO-overzicht" },
];

export function ResultatenPage() {
  const [tab, setTab] = useTab("effect");
  return (
    <AppLayout>
      <PageShell
        eyebrow="JOUW RESULTATEN"
        title="Resultaten"
        subtitle="Effect per pagina en SEO-samenvatting van je top-10."
      >
        <Tabs tabs={RESULTATEN_TABS} current={tab} onChange={setTab} testid="resultaten-tabs" />
        <EmbedShell>
          <TabPanel testid="resultaten-tabs" tabKey={tab}>
            {tab === "effect" && <EffectPage />}
            {tab === "seo"    && <SeoOverviewPage />}
          </TabPanel>
        </EmbedShell>
      </PageShell>
    </AppLayout>
  );
}

// ---------- Markt & concurrenten -------------------------------------------
const MARKT_TABS = [
  { key: "signalen", label: "Signalen" },
  { key: "audit",    label: "Concurrent-audit" },
];

export function MarktEnConcurrentenPage() {
  const [tab, setTab] = useTab("signalen");
  return (
    <AppLayout>
      <PageShell
        eyebrow="JOUW MARKT"
        title="Wat gebeurt er in jouw markt?"
        subtitle="Signalen van concurrenten en snelle audit-check per site."
      >
        <Tabs tabs={MARKT_TABS} current={tab} onChange={setTab} testid="markt-tabs" />
        <EmbedShell>
          <TabPanel testid="markt-tabs" tabKey={tab}>
            {tab === "signalen" && <MarketRadarPage />}
            {tab === "audit"    && <SiteAuditPage />}
          </TabPanel>
        </EmbedShell>
      </PageShell>
    </AppLayout>
  );
}

// ---------- Instellingen ---------------------------------------------------
const INSTELL_TABS = [
  { key: "bedrijfsprofiel", label: "Bedrijfsprofiel" },
  { key: "privacy",         label: "Privacy & data" },
  { key: "budget",          label: "Budget" },
  { key: "team",            label: "Team & toegang" },
];

export function InstellingenPage() {
  const [tab, setTab] = useTab("bedrijfsprofiel");
  return (
    <AppLayout>
      <PageShell
        eyebrow="INSTELLINGEN"
        title="Instellingen"
        subtitle="Bedrijfsprofiel, privacy, budget en toegang."
      >
        <Tabs tabs={INSTELL_TABS} current={tab} onChange={setTab} testid="instellingen-tabs" />
        <EmbedShell>
          <TabPanel testid="instellingen-tabs" tabKey={tab}>
            {tab === "bedrijfsprofiel" && <BedrijfsprofielView />}
            {tab === "privacy"         && <PrivacyVaultPage />}
            {tab === "budget"          && <WalletPage />}
            {tab === "team" && (
              <div className="rounded-[16px] p-10 bg-bg-panel shadow-card text-center"
                    data-testid="instellingen-team-placeholder"
                    style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
                <p className="text-sm text-text-muted mb-2">Team & toegang</p>
                <p className="text-base text-text-primary">Deze functie komt eraan.</p>
              </div>
            )}
          </TabPanel>
        </EmbedShell>
      </PageShell>
    </AppLayout>
  );
}

// ---------- Adviseur (placeholder) -----------------------------------------
export function AdviseurPage() {
  return (
    <AppLayout>
      <PageShell
        eyebrow="MARKETINGADVISEUR"
        title="Vraag je marketingadviseur"
        subtitle="Persoonlijk advies wanneer je vastloopt."
      >
        <div className="rounded-[16px] p-8 bg-bg-panel shadow-card max-w-xl"
              data-testid="adviseur-placeholder"
              style={{ boxShadow: "0 1px 3px rgba(23,44,50,0.06)" }}>
          <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center mb-4">
            <MessageCircleQuestion className="w-6 h-6 text-accent-teal" />
          </div>
          <p className="text-base text-text-primary mb-3">
            Deze functie komt eraan. Je kunt straks direct een vraag stellen aan een adviseur van JustEasySEO.
          </p>
          <p className="text-sm text-text-muted">
            Wil je nu al contact? Stuur een mail naar{" "}
            <a href="mailto:advies@justeasyseo.nl" className="text-accent-teal-dim hover:underline">
              advies@justeasyseo.nl
            </a>.
          </p>
          <div className="mt-6">
            <Link to="/" className="text-sm text-accent-teal-dim hover:underline">← Terug naar overzicht</Link>
          </div>
        </div>
      </PageShell>
    </AppLayout>
  );
}
